import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import ScheduleIcon from '@mui/icons-material/Schedule';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import type { DispatchRun } from '../../types/dispatch';

interface DispatchRunsTableProps {
  runs: DispatchRun[];
  boardsMap: Record<number, string>;
  loading?: boolean;
}

const STATUS_CONFIG: Record<
  string,
  { color: 'success' | 'error' | 'warning' | 'info'; icon: React.ReactNode; label: string }
> = {
  succeeded: {
    color: 'success',
    icon: <CheckCircleIcon fontSize="small" />,
    label: '✓',
  },
  success: {
    color: 'success',
    icon: <CheckCircleIcon fontSize="small" />,
    label: '✓',
  },
  failed: {
    color: 'error',
    icon: <CancelIcon fontSize="small" />,
    label: '✗',
  },
  partial: {
    color: 'warning',
    icon: <WarningAmberIcon fontSize="small" />,
    label: '!',
  },
  running: {
    color: 'info',
    icon: <ScheduleIcon fontSize="small" />,
    label: '...',
  },
};

function truncate(str: string | null, len: number): string {
  if (!str) return '—';
  if (str.length <= len) return str;
  return str.slice(0, len) + '...';
}

function shortRunId(runId: string): string {
  return runId.replace(/-/g, '').slice(0, 8);
}

function formatStartedAt(ts: string | null | undefined): string {
  if (!ts) return '—';
  try {
    const d = new Date(ts);
    const y = d.getFullYear();
    const mo = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const h = String(d.getHours()).padStart(2, '0');
    const m = String(d.getMinutes()).padStart(2, '0');
    return `${y}-${mo}-${day} ${h}:${m}`;
  } catch {
    return ts;
  }
}

const DispatchRunsTable: React.FC<DispatchRunsTableProps> = ({
  runs,
  boardsMap,
  loading,
}) => {
  const navigate = useNavigate();

  const getStatusDisplay = (status: string) => {
    const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.failed;
    return (
      <Chip
        size="small"
        color={cfg.color}
        icon={<>{cfg.icon}</>}
        label={cfg.label}
        sx={{ minWidth: 44 }}
      />
    );
  };

  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow sx={{ bgcolor: 'grey.100' }}>
            <TableCell><strong>Run ID</strong></TableCell>
            <TableCell><strong>Ticket</strong></TableCell>
            <TableCell><strong>Title</strong></TableCell>
            <TableCell><strong>Rule</strong></TableCell>
            <TableCell><strong>Assignee</strong></TableCell>
            <TableCell><strong>Status</strong></TableCell>
            <TableCell><strong>Duration</strong></TableCell>
            <TableCell><strong>Date / Time</strong></TableCell>
            <TableCell><strong>Board</strong></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {runs.map((run) => (
            <TableRow
              key={run.run_id}
              hover
              sx={{ cursor: 'pointer' }}
              onClick={() => navigate(`/dispatch-control-center/${run.run_id}`)}
            >
              <TableCell>
                <Typography
                  variant="body2"
                  fontFamily="monospace"
                  sx={{ fontSize: '0.75rem' }}
                >
                  {shortRunId(run.run_id)}
                </Typography>
              </TableCell>
              <TableCell>#{run.ticket_id}</TableCell>
              <TableCell sx={{ maxWidth: 180 }} title={run.new_title || ''}>
                {truncate(run.new_title, 24)}
              </TableCell>
              <TableCell>{run.dispatch_rule || '—'}</TableCell>
              <TableCell>{run.assignee_name || '—'}</TableCell>
              <TableCell>{getStatusDisplay(run.status)}</TableCell>
              <TableCell>
                {run.duration_ms != null ? `${run.duration_ms}ms` : '—'}
              </TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                {formatStartedAt(run.started_at)}
              </TableCell>
              <TableCell>
                {run.board_id != null
                  ? boardsMap[run.board_id] ?? `#${run.board_id}`
                  : '—'}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default DispatchRunsTable;
